import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { GlobalContext } from './GlobalContext';
import { apiFetch } from './api.js';

/**
 * Who else has the app open right now.
 *
 * A context rather than a hook because two places want the same answer — the
 * header indicator and the Login activity panel in Settings — and two hooks would
 * mean two fetches and two socket handlers for one fact.
 *
 * Mounted beside NotificationProvider in Cars.jsx, which puts both CarsHead and
 * (through CarsNav → SubNav) Settings inside it.
 *
 * THE SERVER IS THE ONLY JUDGE OF WHO IS PRESENT. This holds no timers and does
 * no reasoning about who has gone: it renders the last list it was sent. The
 * grace window, the deduplication of a person's several tabs and the tenant
 * scoping all live in node/helpers/presence.js, because all three are things a
 * client could get wrong in a way that shows one dealership's staff to another.
 */

const PresenceContext = createContext(null);

/** Safe to call outside the provider: presence is ambient, and a screen that
 *  happens to render outside it should show nothing rather than throw. That is
 *  the opposite of useNotifications, which throws — losing a notification is a
 *  bug worth surfacing, whereas an empty presence list is a legitimate state. */
export const usePresence = () => useContext(PresenceContext) || {
  users: [], others: [], count: 0, loading: false,
};

export const PresenceProvider = ({ children }) => {
  const globals = useContext(GlobalContext);
  const socket = globals?.socket || null;
  const dealershipID = globals?.DealershipID;
  const myUserID = globals?.UserID;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!dealershipID) return;
    try {
      const res = await apiFetch('/presence/active', { body: {} });
      const obj = await res.json();
      if (obj.ok) setUsers(obj.users || []);
    } catch {
      //Silent. Presence is decoration on every screen it appears on, and an
      //error box about it would be noise the user cannot act on.
    } finally {
      setLoading(false);
    }
  }, [dealershipID]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => setUsers(payload?.users || []);
    socket.on('presence', handler);
    //A reconnecting socket rejoins its room and the server broadcasts on connect,
    //so the push covers a reconnect. This re-fetch covers the case the push
    //cannot: a socket that was down while people came and went, and whose
    //`connect` fires before the server's broadcast reaches it.
    socket.on('connect', load);
    return () => {
      socket.off('presence', handler);
      socket.off('connect', load);
    };
  }, [socket, load]);

  const value = useMemo(() => {
    //"Who ELSE is here" — the badge counts colleagues, so seeing yourself in it
    //would make a lone user read "1 online" and wonder who.
    const others = users.filter((u) => Number(u.UserID) !== Number(myUserID));
    return { users, others, count: others.length, loading };
  }, [users, myUserID, loading]);

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
};

export default PresenceContext;
